/** ADM-04 Clients · ADM-05 Fiche client (groupe tarifaire, moyens de paiement, suspension ★). */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Choice, Pagination, SearchField, SelectField, Table, TextField, useToast } from '@myron/design-system';
import { Ban, Play, Save } from 'lucide-react';
import type { Page, S } from '@myron/contracts';
import { get, patch, post } from '../../../shared/api/client';
import { SiPermission, useSession } from '../../../shared/auth/session';
import { Confirmation, EntetePage, EtatRequete, ErreurFormulaire, MentionSimule, StatutBadge } from '../../../shared/ui/composants';
import { dateCourte, dateHeure, LIBELLE_MOYEN } from '../../../shared/ui/format';
import { BarreFiltres, useGroupes } from './commun';

type Entreprise = S<'EntrepriseAdmin'>;
type Detail = S<'EntrepriseAdminDetail'> & { utilisateurs?: S<'UtilisateurClient'>[]; adresses?: S<'Adresse'>[]; ibanRemboursementMasque?: string };

export function PageClients() {
  const nav = useNavigate();
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('ACTIVE,SUSPENDUE');
  const [groupe, setGroupe] = useState('');
  const [page, setPage] = useState(1);
  const groupes = useGroupes(useSession().peut('tarif.lire'));
  const q = useQuery({ queryKey: ['admin', 'clients', recherche, statut, groupe, page], queryFn: () => get<Page<Entreprise>>('/admin/clients', { q: recherche, statut, groupeTarifaireId: groupe, page, taille: 25 }) });
  const nomGroupe = (id?: string) => groupes.data?.find((g) => g.id === id)?.code ?? '—';
  return (
    <>
      <EntetePage titre="Clients professionnels" />
      <BarreFiltres>
        <SearchField label="Rechercher un client" placeholder="Raison sociale, référence, SIRET" value={recherche} onChange={(e) => { setRecherche(e.target.value); setPage(1); }} />
        <SelectField label="Statut" value={statut} onChange={(e) => { setStatut(e.target.value); setPage(1); }} options={[{ value: 'ACTIVE,SUSPENDUE', label: 'Actifs et suspendus' }, { value: 'ACTIVE', label: 'Actifs' }, { value: 'SUSPENDUE', label: 'Suspendus' }, { value: '', label: 'Tous' }]} />
        {groupes.data && <SelectField label="Groupe tarifaire" value={groupe} onChange={(e) => { setGroupe(e.target.value); setPage(1); }} options={[{ value: '', label: 'Tous' }, ...groupes.data.map((g) => ({ value: g.id, label: g.code }))]} />}
      </BarreFiltres>
      <EtatRequete requete={q} vide={(d) => d.total === 0} titreVide="Aucun client" texteVide="Modifiez la recherche ou les filtres." enfants={(d) => (
        <>
          <Table<Entreprise> legende="Clients" lignes={d.donnees} cle={(e) => e.id!} onLigne={(e) => nav(`/admin/clients/${e.id}`)} colonnes={[
            { cle: 'ref', titre: 'Référence', rendu: (e) => e.reference },
            { cle: 'rs', titre: 'Raison sociale', rendu: (e) => e.raisonSociale },
            { cle: 'grp', titre: 'Groupe', rendu: (e) => (groupes.data ? nomGroupe(e.groupeTarifaireId) : '—') },
            { cle: 'moy', titre: 'Paiements', rendu: (e) => (e.moyensPaiementAutorises ?? []).map((m) => LIBELLE_MOYEN[m]).join(', ') || '—' },
            { cle: 'st', titre: 'Statut', rendu: (e) => <StatutBadge statut={e.statut!} /> },
            { cle: 'a', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (e) => <Button compact variante="secondaire" onClick={(ev) => { ev.stopPropagation(); nav(`/admin/clients/${e.id}`); }} aria-label={`Ouvrir ${e.raisonSociale}`}>Ouvrir</Button> },
          ]} />
          <Pagination page={d.page} total={d.total} taille={d.taille} onPage={setPage} />
        </>
      )} />
    </>
  );
}

export function PageFicheClient() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const { peut } = useSession();
  const cleQ = ['admin', 'client', id];
  const q = useQuery({ queryKey: cleQ, queryFn: () => get<Detail>(`/admin/clients/${id}`) });
  const groupes = useGroupes(peut('tarif.lire') || peut('client.modifier'));
  const [groupe, setGroupe] = useState('');
  const [moyens, setMoyens] = useState<string[]>([]);
  const [iban, setIban] = useState('');
  const [suspendre, setSuspendre] = useState(false);
  useEffect(() => {
    if (q.data) { setGroupe(q.data.groupeTarifaireId ?? ''); setMoyens(q.data.moyensPaiementAutorises ?? []); }
  }, [q.data]);
  const maj = (d: Detail) => { qc.setQueryData(cleQ, d); void qc.invalidateQueries({ queryKey: ['admin', 'clients'] }); };

  const enregistrer = useMutation({
    mutationFn: () => patch<Detail>(`/admin/clients/${id}`, { groupeTarifaireId: groupe || null, moyensPaiementAutorises: moyens, ...(iban ? { ibanRemboursement: iban.replace(/\s/g, '') } : {}) }, { version: q.data!.version }),
    onSuccess: (d) => { maj(d); setIban(''); toast({ ton: 'succes', titre: 'Client enregistré' }); },
  });
  const suspension = useMutation({
    mutationFn: (motif: string) => post<Detail>(`/admin/clients/${id}/suspendre`, { motif }, { version: q.data!.version }),
    onSuccess: (d) => { maj(d); setSuspendre(false); toast({ ton: 'alerte', titre: 'Client suspendu', texte: 'Les sessions de ses utilisateurs ont été révoquées.' }); },
  });
  const reactivation = useMutation({
    mutationFn: () => post<Detail>(`/admin/clients/${id}/reactiver`, undefined, { version: q.data!.version }),
    onSuccess: (d) => { maj(d); toast({ ton: 'succes', titre: 'Client réactivé' }); },
  });

  return (
    <EtatRequete requete={q} enfants={(e) => (
      <>
        <EntetePage titre={e.raisonSociale!} fil={[{ libelle: 'Clients', vers: '/admin/clients' }, { libelle: e.reference ?? '' }]} sousTitre={<StatutBadge statut={e.statut!} />} actions={
          <SiPermission permission="client.suspendre">
            {e.statut === 'ACTIVE' && <Button variante="danger" icone={Ban} onClick={() => setSuspendre(true)}>Suspendre</Button>}
            {e.statut === 'SUSPENDUE' && <Button variante="secondaire" icone={Play} chargement={reactivation.isPending} onClick={() => reactivation.mutate()}>Réactiver</Button>}
          </SiPermission>
        } />
        <ErreurFormulaire erreur={reactivation.error} />
        {e.statut === 'SUSPENDUE' && <Alert ton="alerte" titre="Compte suspendu">{e.motifSuspension ?? 'Commandes et prix bloqués ; historique consultable par le client.'}</Alert>}
        <div className="my-grille-2" style={{ marginTop: 'var(--space-4)' }}>
          <div className="my-pile">
            <Card as="section" aria-labelledby="t-id">
              <h2 id="t-id" className="my-titre-4">Identification</h2>
              <dl className="my-dl" style={{ marginTop: 12 }}>
                <dt>SIRET</dt><dd className="my-num">{e.siret}</dd>
                <dt>NAF</dt><dd>{e.codeNaf ?? '—'}</dd>
                <dt>TVA intracommunautaire</dt><dd>{e.tvaIntracom ?? '—'}</dd>
                <dt>Client depuis</dt><dd>{dateCourte(e.valideeLe ?? e.creeLe)}</dd>
              </dl>
            </Card>
            <Card as="section" aria-labelledby="t-u">
              <h2 id="t-u" className="my-titre-4">Utilisateurs</h2>
              <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0, marginTop: 12 }}>
                {(e.utilisateurs ?? []).map((u) => (
                  <li key={u.id} className="my-ligne my-ligne--espace">
                    <span>{u.prenom} {u.nom} <span className="my-secondaire my-petit">— {u.email}</span></span>
                    <span className="my-ligne"><StatutBadge statut={u.statut === 'ACTIF' ? 'ACTIF' : u.statut!} />{u.mfaActive && <span className="my-badge my-badge--info">MFA</span>}<span className="my-legende">{dateHeure(u.derniereConnexionLe)}</span></span>
                  </li>
                ))}
              </ul>
            </Card>
            <Card as="section" aria-labelledby="t-a">
              <h2 id="t-a" className="my-titre-4">Adresses</h2>
              <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0, marginTop: 12 }}>
                {(e.adresses ?? []).map((a) => <li key={a.id}><strong>{a.type === 'FACTURATION' ? 'Facturation' : 'Livraison'}</strong> — {a.ligne1}, {a.codePostal} {a.ville}</li>)}
              </ul>
            </Card>
          </div>
          <Card as="section" aria-labelledby="t-com">
            <h2 id="t-com" className="my-titre-4">Conditions commerciales</h2>
            {peut('client.modifier') ? (
              <form className="my-form" style={{ marginTop: 12 }} onSubmit={(ev) => { ev.preventDefault(); enregistrer.mutate(); }}>
                <SelectField label="Groupe tarifaire" value={groupe} onChange={(ev) => setGroupe(ev.target.value)} options={[{ value: '', label: 'Prix de base (aucun groupe)' }, ...(groupes.data ?? []).map((g) => ({ value: g.id, label: `${g.code} — ${g.libelle}` }))]} />
                <fieldset className="my-pile my-pile--serre" style={{ border: 0, padding: 0, margin: 0 }}>
                  <legend className="my-field__label">Moyens de paiement autorisés</legend>
                  {['VIREMENT', 'CB_EN_LIGNE', 'CB_RETRAIT'].map((m) => (
                    <Choice key={m} label={LIBELLE_MOYEN[m]} checked={moyens.includes(m)} onChange={(ev) => setMoyens((l) => (ev.target.checked ? [...l, m] : l.filter((x) => x !== m)))} />
                  ))}
                </fieldset>
                <TextField label="IBAN de remboursement" aide={<>Actuel : {e.ibanRemboursementMasque ?? 'non renseigné'} — champ sensible, jamais réaffiché en clair. <MentionSimule>chiffrement SIMULÉ</MentionSimule></>} value={iban} onChange={(ev) => setIban(ev.target.value)} autoComplete="off" />
                <ErreurFormulaire erreur={enregistrer.error} />
                <Button type="submit" icone={Save} chargement={enregistrer.isPending}>Enregistrer</Button>
              </form>
            ) : (
              <dl className="my-dl" style={{ marginTop: 12 }}>
                <dt>Groupe</dt><dd>{groupes.data?.find((g) => g.id === e.groupeTarifaireId)?.libelle ?? (e.groupeTarifaireId ? 'Groupe défini' : 'Prix de base')}</dd>
                <dt>Paiements</dt><dd>{(e.moyensPaiementAutorises ?? []).map((m) => LIBELLE_MOYEN[m]).join(', ') || '—'}</dd>
              </dl>
            )}
          </Card>
        </div>
        <Confirmation ouverte={suspendre} titre="Suspendre ce client" texte="Ses utilisateurs seront déconnectés immédiatement et ne pourront plus commander ni voir leurs prix." libelleAction="Suspendre" danger motif="obligatoire" chargement={suspension.isPending} erreur={suspension.error} onFermer={() => setSuspendre(false)} onConfirmer={(m) => suspension.mutate(m)} />
      </>
    )} />
  );
}
